/**
 * Batch #14.168 — SuiviSignaturesTile (remplace "Bail en attente").
 *
 * Vue unifiée des workflows de signature en cours / récents pour le master :
 *   - Bail TAL (collection bail_signature_workflows)
 *   - Avis renouvellement (collection rent_increase_signatures)
 *   - Baux legacy (collection immeuble_lease_signature_requests)
 *
 * Affiche pour chaque workflow : type, titre, étape actuelle (à qui on est rendu),
 * statut de chaque signataire, lien Drive si archivé.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  Loader2, Clock, CheckCircle2, XCircle, FileText, FileSignature,
  TrendingUp, ExternalLink, RefreshCw, Mail, Filter, Send, AlertCircle,
} from "lucide-react";
import { toast } from "@/lib/toast";
import NotificationTimeline from "@/components/admin/NotificationTimeline";
import ArchiveButton from "@/components/admin/ArchiveButton";
import { API, authH } from "@/api";


const KIND_META = {
  bail_tal: { Icon: FileSignature, color: "indigo", label: "Bail TAL" },
  rent_increase: { Icon: TrendingUp, color: "rose", label: "Renouvellement" },
  lease_legacy: { Icon: FileText, color: "slate", label: "Bail (legacy)" },
};

const STATUS_META = {
  draft: { label: "Brouillon", color: "slate", Icon: FileText },
  pending_tenant: { label: "Locataire à signer", color: "amber", Icon: Clock },
  pending_tenants: { label: "Locataires à signer", color: "amber", Icon: Clock },
  pending_landlord: { label: "Finalisation en cours", color: "amber", Icon: Clock },
  tenants_signed: { label: "Tous locataires OK", color: "amber", Icon: Clock },
  sent: { label: "Envoyé pour signature", color: "amber", Icon: Mail },
  in_progress: { label: "En cours", color: "amber", Icon: Clock },
  awaiting_signatures: { label: "Signatures en attente", color: "amber", Icon: Clock },
  completed: { label: "Complété", color: "emerald", Icon: CheckCircle2 },
  cancelled: { label: "Annulé", color: "slate", Icon: XCircle },
};

const COLOR_BG = {
  indigo: "bg-indigo-50 border-indigo-200 text-indigo-700",
  rose: "bg-rose-50 border-rose-200 text-rose-700",
  slate: "bg-slate-50 border-slate-200 text-slate-700",
  amber: "bg-amber-50 border-amber-200 text-amber-700",
  emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("fr-CA", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

export default function SuiviSignaturesTile({ masterId }) {
  const [data, setData] = useState({ items: [], by_status: {}, by_kind: {} });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | in_progress | completed | bail_tal | rent_increase
  const [openItem, setOpenItem] = useState(null);
  const [relancingId, setRelancingId] = useState(null);

  const load = useCallback(async () => {
    if (!masterId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/immeuble/${masterId}/signature-workflows`, { headers: authH() });
      setData(r.data || { items: [], by_status: {}, by_kind: {} });
    } catch {
      toast.error("Impossible de charger le suivi des signatures");
    } finally { setLoading(false); }
  }, [masterId]);

  useEffect(() => { load(); }, [load]);

  // Batch #14.169 — Relance manuelle des signatures en attente
  const relance = async (it, e) => {
    e?.stopPropagation();
    const kindEndpoint = it.kind === "bail_tal" ? "bail_tal" : it.kind === "rent_increase" ? "rent-increase" : null;
    if (!kindEndpoint) {
      toast.error("Relance non supportée pour ce type de workflow");
      return;
    }
    if (!window.confirm(
      `Renvoyer le lien de signature aux signataires en attente pour :\n\n` +
      `« ${it.title} »\n\n` +
      `Étape actuelle : ${it.current_step || "—"}\n` +
      `${it.relance_count ? `(déjà relancé ${it.relance_count} fois)` : "(1re relance)"}`
    )) return;
    setRelancingId(it.id);
    try {
      const r = await axios.post(
        `${API}/admin/signature-workflows/${kindEndpoint}/${it.id}/relance`,
        { origin_url: window.location.origin },
        { headers: authH() }
      );
      toast.success(r.data?.message || "Lien renvoyé");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la relance");
    } finally {
      setRelancingId(null);
    }
  };

  const items = data.items || [];
  const filtered = items.filter((it) => {
    if (filter === "all") return true;
    if (filter === "in_progress") return !["completed", "cancelled"].includes(it.status);
    if (filter === "completed") return it.status === "completed";
    return it.kind === filter;
  });

  return (
    <div className="bg-white rounded-2xl border border-rose-200 shadow-sm overflow-hidden" data-testid="suivi-signatures-tile">
      {/* Header */}
      <div className="px-5 py-4 bg-gradient-to-r from-rose-50 to-rose-100 border-b border-rose-200 flex items-center gap-3">
        <Clock className="w-6 h-6 text-rose-700" />
        <div className="flex-1">
          <h2 className="text-base font-bold text-slate-900">Suivi des actions signature</h2>
          <p className="text-xs text-slate-600 mt-0.5">
            Bail TAL · Avis renouvellement · À qui est rendue chaque signature
          </p>
        </div>
        <button
          onClick={load}
          className="p-2 rounded-lg hover:bg-white/60 transition"
          aria-label="Actualiser"
          data-testid="suivi-signatures-refresh"
        >
          <RefreshCw className={`w-4 h-4 text-slate-600 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {/* Stats + filtres */}
      <div className="px-5 py-3 border-b border-slate-100 bg-slate-50/60 flex flex-wrap items-center gap-2 text-[11px]">
        <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-amber-100 text-amber-800 font-semibold">
          <Clock className="w-3 h-3" />
          En cours : {data.by_status?.in_progress || 0}
        </div>
        <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-emerald-100 text-emerald-800 font-semibold">
          <CheckCircle2 className="w-3 h-3" />
          Complétés : {data.by_status?.completed || 0}
        </div>
        <div className="flex-1" />
        <Filter className="w-3.5 h-3.5 text-slate-400" />
        <div className="flex flex-wrap items-center gap-1">
          {[
            ["all", "Tous"],
            ["in_progress", "En cours"],
            ["completed", "Complétés"],
            ["bail_tal", "Bail TAL"],
            ["rent_increase", "Renouvellement"],
          ].map(([k, label]) => (
            <button
              key={k}
              onClick={() => setFilter(k)}
              className={`px-2 py-0.5 rounded-full font-medium text-[10px] ${
                filter === k
                  ? "bg-rose-600 text-white"
                  : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"
              }`}
              data-testid={`suivi-signatures-filter-${k}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Liste */}
      <div className="divide-y divide-slate-100">
        {loading && (
          <div className="px-5 py-10 text-center text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin inline mr-2" />
            Chargement…
          </div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="px-5 py-10 text-center text-slate-500 text-sm" data-testid="suivi-signatures-empty">
            <CheckCircle2 className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
            Aucun workflow {filter === "all" ? "" : `dans la catégorie « ${filter} »`}.
          </div>
        )}
        {!loading && filtered.map((it) => {
          const kindMeta = KIND_META[it.kind] || KIND_META.lease_legacy;
          const statusMeta = STATUS_META[it.status] || { label: it.status, color: "slate", Icon: FileText };
          const KindIcon = kindMeta.Icon;
          const StatusIcon = statusMeta.Icon;
          const isOpen = openItem === it.id;
          const isInProgress = !["completed", "cancelled"].includes(it.status);
          const days = it.days_pending || 0;
          // Couleur du badge "X jours" : vert <3, ambre 3-7, rouge >7
          const daysColor = days > 7
            ? "bg-rose-100 text-rose-700 border-rose-300"
            : days >= 3
              ? "bg-amber-100 text-amber-700 border-amber-300"
              : "bg-emerald-100 text-emerald-700 border-emerald-300";
          const canRelance = isInProgress && (it.kind === "bail_tal" || it.kind === "rent_increase");
          return (
            <div key={it.id} className="px-5 py-3 hover:bg-slate-50/60" data-testid={`suivi-signatures-row-${it.id}`}>
              <div
                className="flex items-center gap-3 cursor-pointer"
                onClick={() => setOpenItem(isOpen ? null : it.id)}
              >
                {/* Icône kind */}
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center border ${COLOR_BG[kindMeta.color] || COLOR_BG.slate}`}>
                  <KindIcon className="w-5 h-5" />
                </div>
                {/* Title + étape */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm text-slate-900 truncate">{it.title}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${COLOR_BG[kindMeta.color]?.split(' ').slice(0, 2).join(' ') || ''}`}>
                      {kindMeta.label}
                    </span>
                    {/* Batch #14.169 — Indicateur de durée pour les workflows en cours */}
                    {isInProgress && (
                      <span
                        className={`text-[10px] px-1.5 py-0.5 rounded border inline-flex items-center gap-1 font-semibold ${daysColor}`}
                        data-testid={`suivi-days-${it.id}`}
                      >
                        {days > 7 && <AlertCircle className="w-2.5 h-2.5" />}
                        {days === 0 ? "Aujourd'hui" : days === 1 ? "1 jour" : `${days} jours`}
                      </span>
                    )}
                    {(it.relance_count || 0) > 0 && (
                      <span
                        className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200"
                        title={`Dernière relance le ${fmtDate(it.last_relance_at)}`}
                      >
                        ↻ {it.relance_count}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 mt-0.5 text-[11px] text-slate-600">
                    <StatusIcon className="w-3 h-3" />
                    <span className="font-medium" data-testid={`suivi-status-${it.id}`}>{statusMeta.label}</span>
                    {it.current_step && it.current_step !== "—" && (
                      <>
                        <span className="text-slate-300">·</span>
                        <span className="text-slate-700">{it.current_step}</span>
                      </>
                    )}
                  </div>
                </div>
                {/* Actions : Relancer + Drive link */}
                <div className="flex items-center gap-2">
                  {canRelance && (
                    <button
                      onClick={(e) => relance(it, e)}
                      disabled={relancingId === it.id}
                      className={`text-[10px] inline-flex items-center gap-1 px-2 py-1 rounded font-semibold transition disabled:opacity-50 ${
                        days > 7
                          ? "bg-rose-600 hover:bg-rose-700 text-white"
                          : "bg-indigo-50 hover:bg-indigo-100 text-indigo-700"
                      }`}
                      title="Renvoyer le lien de signature au(x) signataire(s) en attente"
                      data-testid={`suivi-relance-${it.id}`}
                    >
                      {relancingId === it.id ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <Send className="w-3 h-3" />
                      )}
                      Relancer
                    </button>
                  )}
                  {it.drive_link && (
                    <a
                      href={it.drive_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-[10px] inline-flex items-center gap-1 px-2 py-1 rounded bg-blue-50 text-blue-700 hover:bg-blue-100"
                      data-testid={`suivi-drive-link-${it.id}`}
                    >
                      <ExternalLink className="w-3 h-3" />
                      Drive
                    </a>
                  )}
                  {(it.kind === "bail_tal" || it.kind === "rent_increase") && (
                    <span onClick={(e) => e.stopPropagation()}>
                      <ArchiveButton
                        itemType={it.kind === "bail_tal" ? "bail_signature" : "rent_increase"}
                        itemId={it.id}
                        itemLabel="ce workflow"
                        onArchived={load}
                        size="xs"
                      />
                    </span>
                  )}
                  <span className="text-[10px] text-slate-400 hidden sm:inline">{fmtDate(it.updated_at || it.created_at)}</span>
                </div>
              </div>

              {/* Détails (expanded) */}
              {isOpen && (
                <div className="mt-3 ml-13 pl-13 border-l-2 border-slate-200 space-y-1.5" data-testid={`suivi-details-${it.id}`}>
                  {(it.signers || []).map((s, idx) => {
                    const signed = !!s.signed_at;
                    return (
                      <div key={idx} className="flex items-center gap-2 text-[11px]">
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                          signed ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
                        }`}>
                          {signed ? <CheckCircle2 className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                        </div>
                        <span className="font-medium text-slate-800">{s.name}</span>
                        <span className="text-slate-400">({s.role})</span>
                        {s.decision && (
                          <span className={`px-1.5 py-0 rounded text-[9px] ${
                            s.decision === "accepte" ? "bg-emerald-100 text-emerald-700"
                            : s.decision === "refuse" ? "bg-rose-100 text-rose-700"
                            : "bg-slate-100 text-slate-600"
                          }`}>
                            {s.decision === "accepte" ? "Accepté" : s.decision === "refuse" ? "Refusé" : s.decision}
                          </span>
                        )}
                        {signed ? (
                          <span className="text-slate-500 ml-auto">Signé le {fmtDate(s.signed_at)}</span>
                        ) : (
                          <span className="text-amber-600 ml-auto italic">en attente</span>
                        )}
                      </div>
                    );
                  })}
                  <div className="text-[10px] text-slate-400 pt-1">
                    Créé le {fmtDate(it.created_at)}{it.updated_at && it.updated_at !== it.created_at ? ` · MAJ ${fmtDate(it.updated_at)}` : ""}
                  </div>
                  {/* Batch #14.197 — Timeline notifications */}
                  {(it.kind === "bail_tal" || it.kind === "rent_increase") && (
                    <div className="pt-2 mt-2 border-t border-slate-100">
                      <NotificationTimeline relatedType={it.kind} relatedId={it.id} compact />
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
